import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

const statusFilters = [
  { value: "", label: "All open" },
  { value: "requested", label: "Requested" },
  { value: "outreach_completed", label: "Outreach completed" },
  { value: "no_contact_info", label: "No contact info" },
  { value: "email_delivery_failed", label: "Email delivery failed" },
];

const statusLabels = {
  requested: "Requested",
  outreach_completed: "Outreach completed",
  no_contact_info: "No contact info",
  email_delivery_failed: "Email delivery failed",
};

export default function VerificationOperationsFinal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [forms, setForms] = useState({});
  const [assignedToMe, setAssignedToMe] = useState(false);
  const statusFilter = searchParams.get("status") || "";
  const token = localStorage.getItem("coi_platform_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    const params = {};
    if (statusFilter) params.status_filter = statusFilter;
    if (assignedToMe) params.assigned_to_me = true;
    return api.get("/verifications/staff/queue", { headers, params }).then((r) => setItems(r.data));
  }, [statusFilter, assignedToMe]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("status", value); else next.delete("status");
    setSearchParams(next);
  };

  const set = (id, key, value) => setForms({ ...forms, [id]: { ...(forms[id] || {}), [key]: value } });

  async function contact(id, method) {
    try {
      const f = forms[id] || {};
      await api.post(`/verifications/${id}/outreach`, { method, recipient: f.contactTarget || "", notes: f.outreachNotes || "" }, { headers });
      load();
    } catch (e) { alert(formatApiError(e)); }
  }
  async function outcome(id, status) {
    try {
      const f = forms[id] || {};
      await api.post(`/verifications/${id}/outcome`, { status, notes: f.notes || "", verifiedWith: { name: f.name || "", company: f.company || "", contact: f.contact || "" } }, { headers });
      load();
    } catch (e) { alert(formatApiError(e)); }
  }

  return (
    <section data-testid="verification-ops-page">
      <h1 className="text-2xl font-semibold">Policy verification queue</h1>
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="verification-queue-filters">
        {statusFilters.map((filter) => (
          <button
            className={`rounded-full border px-3 py-1.5 text-sm transition ${statusFilter === filter.value ? "border-blue-700 bg-blue-700 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`}
            data-testid={`queue-filter-${filter.value || "all"}`}
            key={filter.value || "all"}
            onClick={() => setStatus(filter.value)}
            type="button"
          >
            {filter.label}
          </button>
        ))}
        <label className="ml-2 inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input checked={assignedToMe} data-testid="queue-filter-assigned-to-me" onChange={(e) => setAssignedToMe(e.target.checked)} type="checkbox" />
          Assigned to me
        </label>
      </div>
      <p className="mt-3 text-sm text-slate-500" data-testid="verification-queue-count">{items.length} verification{items.length === 1 ? "" : "s"} in view</p>
      <div className="mt-4 space-y-4">
        {items.map((item) => {
          const f = forms[item.id] || {};
          return (
            <article className="rounded-lg border bg-white p-5" data-testid={`verification-item-${item.id}`} key={item.id}>
              <h2 className="font-semibold">{item.insured?.name || "Insured"}</h2>
              <p className="text-sm text-slate-500" data-testid={`verification-status-${item.id}`}>{statusLabels[item.status] || item.status}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Field placeholder="Agent/carrier email or phone" value={f.contactTarget || ""} onChange={(v) => set(item.id, "contactTarget", v)} />
                <Field placeholder="Contact notes" value={f.outreachNotes || ""} onChange={(v) => set(item.id, "outreachNotes", v)} />
              </div>
              <div className="mt-3 flex gap-2">
                <Button onClick={() => contact(item.id, "email")} variant="outline">Send verification email</Button>
                <Button onClick={() => contact(item.id, "phone")} variant="outline">Log phone verification</Button>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <Field placeholder="Verifier name" value={f.name || ""} onChange={(v) => set(item.id, "name", v)} />
                <Field placeholder="Company" value={f.company || ""} onChange={(v) => set(item.id, "company", v)} />
                <Field placeholder="Email or phone" value={f.contact || ""} onChange={(v) => set(item.id, "contact", v)} />
              </div>
              <Field placeholder="Outcome notes" value={f.notes || ""} onChange={(v) => set(item.id, "notes", v)} />
              <div className="mt-3 flex gap-2">
                <Button onClick={() => outcome(item.id, "verified")}>Mark verified</Button>
                <Button onClick={() => outcome(item.id, "unable_to_verify")} variant="outline">Unable to verify</Button>
                <Button onClick={() => outcome(item.id, "policy_inactive")} variant="outline">Policy inactive</Button>
              </div>
            </article>
          );
        })}
        {!items.length && <p className="text-sm text-slate-500" data-testid="verification-queue-empty">No verification contacts match this filter.</p>}
      </div>
    </section>
  );
}
function Field({ placeholder, value, onChange }) {
  return <Input className="mt-3" onChange={(e) => onChange(e.target.value)} placeholder={placeholder} value={value} />;
}
